import { Outlet, useLocation } from '@tanstack/react-router';
import { createContext, Suspense, use, useMemo, useState, type ReactNode } from 'react';

import { ScreenSkeleton } from '@/shared/page';

import {
  INSS_PAYROLL_PATH,
  PERSONAL_CREDIT_PATH,
  PRIVATE_CDC_PAYROLL_PATH,
  PRIVATE_PAYROLL_PATH,
  PUBLIC_PAYROLL_PATH,
  VEHICLE_PATH,
} from './simulation.screen';

// Rota de layout de `/ccb/contratar` (W02-B, U53; arquivo leve, JS inicial): os caminhos de hoje continuam, e o
// layout guarda o que o caminho já decidiu (produto e modalidade) para o hub mostrar a escolha ao voltar pelo
// "Alterar", mais o `sub` de quem está logado para o rascunho do fluxo (W08). A sessão entra pela rota (a casca);
// o módulo não importa `src/app`.

export interface HireJourney {
  /** `sub` de quem está logado: o rascunho do fluxo é por usuário; sem ele, não grava. */
  readonly user: string | undefined;
  /** O último produto do Contratar (pelo caminho). */
  readonly product: string | undefined;
  /** A última modalidade do produto (pelo caminho). */
  readonly modality: string | undefined;
}

const HireJourneyContext = createContext<HireJourney>({ user: undefined, product: undefined, modality: undefined });

/** O que a rota de layout do Contratar sabe; fora dela (teste direto da tela), tudo vazio. */
export function useHireJourney(): HireJourney {
  return use(HireJourneyContext);
}

// CDC antes do privado: o caminho dele começa com o do privado.
const PRODUCT_PATHS: readonly (readonly [string, string])[] = [
  [PUBLIC_PAYROLL_PATH, 'consignadoPublico'],
  [PRIVATE_CDC_PAYROLL_PATH, 'consignadoPrivadoCDC'],
  [PRIVATE_PAYROLL_PATH, 'consignadoPrivado'],
  [INSS_PAYROLL_PATH, 'consignadoInss'],
  [PERSONAL_CREDIT_PATH, 'creditoPessoal'],
  [VEHICLE_PATH, 'veiculo'],
];

// O slug da modalidade no caminho de cada produto → o id da escolha no hub (`simulation-hire.journey.ts`).
const MODALITY_SLUGS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  consignadoPublico: { novo: 'novo', 'novo-compra-de-divida': 'compraDeDivida', refinanciamento: 'refinanciamento' },
  consignadoPrivado: { novo: 'novo', refinanciamento: 'refinanciamento' },
  consignadoInss: { novo: 'novo', refinanciamento: 'refinanciamento' },
  veiculo: { 'car-equity': 'carEquity', financiamento: 'financiamento' },
};

/** O produto e a modalidade que o caminho do Contratar já decidiu. */
export function hireJourneyOf(pathname: string, simulationMode?: unknown): { readonly product: string | undefined; readonly modality: string | undefined } {
  const path = pathname.replace(/\/+$/, '');
  const match = PRODUCT_PATHS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  if (!match) return { product: undefined, modality: undefined };
  const [prefix, product] = match;
  if (product === 'creditoPessoal' && simulationMode === 'fgts') return { product: 'fgts', modality: undefined };
  const slug = path.slice(prefix.length + 1).split('/')[0] ?? '';
  return { product, modality: MODALITY_SLUGS[product]?.[slug] };
}

/** O provedor sem o `Outlet`, para o harness e para quem monta a tela fora da rota. */
export function HireJourneyProvider({ user, children }: { readonly user?: string | undefined; readonly children: ReactNode }) {
  const location = useLocation();
  const now = hireJourneyOf(location.pathname, (location.search as Readonly<Record<string, unknown>>)['simulationMode']);
  const [last, setLast] = useState<{ readonly product: string | undefined; readonly modality: string | undefined }>(now);
  // Estado derivado do caminho (padrão do React para guardar o anterior): o hub, sem produto na URL, lê o último.
  const next = now.product === undefined ? last : { product: now.product, modality: now.modality ?? (now.product === last.product ? last.modality : undefined) };
  if (next.product !== last.product || next.modality !== last.modality) setLast(next);
  const value = useMemo<HireJourney>(() => ({ user, product: next.product, modality: next.modality }), [user, next.product, next.modality]);
  return <HireJourneyContext value={value}>{children}</HireJourneyContext>;
}

/** A rota de layout do Contratar: o provedor e as telas, com o esqueleto do fluxo enquanto uma chega. */
export function HireLayoutRouteScreen({ user }: { readonly user?: string | undefined }) {
  return (
    <HireJourneyProvider user={user}>
      <Suspense fallback={<ScreenSkeleton archetype="flow" />}>
        <Outlet />
      </Suspense>
    </HireJourneyProvider>
  );
}
